import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { Constant } from '@/config/constants';

// Package interface based on API schema
interface Package {
  id: number;
  name: string;
  description: string;
  maxDucks: number;
  price: number;
  pricePerDuck: number;
  currency: string;
  allowsAdditionalDucks: boolean; // Add this field to determine if additional ducks can be purchased
}

export const usePackagesStore = defineStore('packages', () => {
  const packages = ref<Package[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchTime = ref<number | null>(null);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Check if cache is still valid
  const isCacheValid = computed(() => {
    if (!lastFetchTime.value) return false;
    return Date.now() - lastFetchTime.value < CACHE_DURATION;
  });

  // Sorted packages by price
  const sortedPackages = computed(() => {
    return [...packages.value].sort((a, b) => a.pricePerDuck - b.pricePerDuck);
  });

  // Fetch packages from API
  const fetchPackages = async (forceRefresh = false) => {
    // Return cached data if still valid and not forcing refresh
    if (isCacheValid.value && packages.value.length > 0 && !forceRefresh) {
      return packages.value;
    }

    try {
      loading.value = true;
      error.value = null;

      // Make request without authentication headers for public endpoint
      const response = await axios.get(`${Constant.API_URL}${Constant.PACKAGES}`, {
        headers: {
          // Clear any auth header to ensure the request works even with expired tokens
          'Authorization': ''
        }
      });

      packages.value = response.data;
      lastFetchTime.value = Date.now();
      return packages.value;
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch packages';
      console.error('Error fetching packages:', err);
      return [];
    } finally {
      loading.value = false;
    }
  };

  return {
    packages,
    sortedPackages,
    loading,
    error,
    fetchPackages
  };
});